import { useAuth } from 'features/auth/presentation/context/AuthContext'
import { useEffect, useRef, useState } from 'react'
import { ChevronDownIcon, LogOutIcon, MenuIcon, MoonIcon, SunIcon } from '../Icons'
import { NotificationDropdown } from './NotificationDropdown'

export const Navbar = ({ onMenuClick }: { onMenuClick?: () => void }) => {
  const { user, logout } = useAuth()
  const [dropdownOpen, setDropdownOpen] = useState(false)
  const dropdownRef = useRef<HTMLDivElement>(null)

  // Theme state: default to dark for the stunning visual look
  const [isDark, setIsDark] = useState(() => {
    const saved = localStorage.getItem('sanjeevani_theme')
    return saved ? saved === 'dark' : true
  })

  // Apply theme class
  useEffect(() => {
    if (isDark) {
      document.documentElement.classList.add('dark')
      localStorage.setItem('sanjeevani_theme', 'dark')
    } else {
      document.documentElement.classList.remove('dark')
      localStorage.setItem('sanjeevani_theme', 'light')
    }
  }, [isDark])

  // Close dropdown on click outside
  useEffect(() => {
    const handleOutsideClick = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setDropdownOpen(false)
      }
    }

    document.addEventListener('mousedown', handleOutsideClick)
    return () => document.removeEventListener('mousedown', handleOutsideClick)
  }, [])

  const getInitials = (name: string) => {
    if (!name) return '—'
    return name
      .split(' ')
      .map((part) => part[0])
      .join('')
      .toUpperCase()
      .substring(0, 2)
  }

  return (
    <header className="flex h-16 w-full items-center gap-3 justify-between border-b border-border bg-surface px-4 text-text-primary flex-shrink-0">
      {/* Mobile Menu Toggle Button */}
      <button
        onClick={onMenuClick}
        className="grid h-9 w-9 place-items-center rounded-btn text-text-secondary hover:bg-bg hover:text-text-primary md:hidden flex-shrink-0"
        title="Open menu"
      >
        <MenuIcon size={20} />
      </button>

      {/* Spacer when on desktop so right controls remain right-aligned */}
      <div className="flex-1" />

      {/* Right Controls Area */}
      <div className="flex items-center gap-1.5">
        {/* Theme Switcher Button */}
        <button
          onClick={() => setIsDark(!isDark)}
          className="rounded-full p-2 text-text-secondary hover:bg-bg hover:text-text-primary transition-colors focus:outline-none"
          title={isDark ? 'Switch to Light Mode' : 'Switch to Dark Mode'}
        >
          {isDark ? <SunIcon size={20} /> : <MoonIcon size={20} />}
        </button>

        {/* Notifications Icon with Dropdown Trigger */}
        <NotificationDropdown />

        {/* Profile Card / Dropdown Trigger */}
        <div className="relative" ref={dropdownRef}>
          <button
            onClick={() => setDropdownOpen(!dropdownOpen)}
            className="flex items-center gap-3 rounded-btn p-1.5 hover:bg-bg transition-colors duration-200 focus:outline-none"
          >
            {/* Initials Avatar */}
            <div className="flex h-9 w-9 items-center justify-center rounded-full bg-primary/10 text-sm font-bold text-primary border border-border">
              {getInitials(user?.name || '')}
            </div>

            {/* User Details */}
            <div className="hidden flex-col text-left md:flex">
              <span className="text-xs font-bold leading-tight">{user?.name || '—'}</span>
              <span className="text-[10px] font-semibold text-text-secondary capitalize">{user?.role || '—'}</span>
            </div>

            <ChevronDownIcon
              size={14}
              className={`text-text-secondary transition-transform duration-200 hidden md:block ${
                dropdownOpen ? 'rotate-180' : ''
              }`}
            />
          </button>

          {/* Profile Dropdown Menu */}
          {dropdownOpen && (
            <div className="absolute right-0 mt-2 w-56 origin-top-right rounded-card border border-border bg-surface p-2 shadow-card ring-1 ring-black ring-opacity-5 animate-scaleUp z-30">
              <div className="border-b border-border px-3 py-2 text-left">
                <p className="text-[10px] font-bold uppercase tracking-wider text-text-secondary">Logged In As</p>
                <p className="truncate text-xs font-semibold text-text-primary mt-0.5">{user?.email || '—'}</p>
              </div>
              <div className="mt-1 space-y-1">
                <button
                  onClick={() => {
                    setDropdownOpen(false)
                    logout()
                  }}
                  className="flex w-full items-center gap-3 rounded-btn px-3 py-2 text-left text-xs font-semibold text-danger hover:bg-danger/10 transition-colors duration-150"
                >
                  <LogOutIcon size={16} />
                  Sign Out
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </header>
  )
}
